import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, ChevronLeft, ChevronRight } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import StatusBadge from "../components/StatusBadge";
import { PageLoading, EmptyState, ErrorState } from "../components/States";
import { api } from "../lib/api";
import { fmtDateTime, fmtMoney, setPageTitle } from "../lib/format";

export default function Invoices() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  const load = (p = page) => {
    setError(null);
    api.myInvoices(p).then(setData).catch((e) => { setError(e.message); setData({ items: [], total: 0, page_size: 25 }); });
  };
  useEffect(() => { setPageTitle("Invoices"); load(1); }, []); // eslint-disable-line

  const rows = data?.items || [];
  const pages = Math.max(1, Math.ceil((data?.total || 0) / (data?.page_size || 25)));

  return (
    <div className="min-h-screen flex flex-col" data-testid="invoices-page">
      <Navbar />
      <main className="flex-1 max-w-4xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16">
        <p className="section-label">Driver</p>
        <h1 className="type-display mt-3">Invoices &amp; receipts</h1>
        <p className="mt-1 text-sm text-zinc-400">Immutable receipts for completed payments. Historical amounts never change.</p>
        <div className="mt-8">
          {error ? <ErrorState body={error} onRetry={() => load()} /> : data === null ? <PageLoading label="Loading invoices…" /> :
            rows.length === 0 ? (
              <EmptyState icon={<FileText className="w-7 h-7 text-[#FF3B45]" />} title="No invoices yet"
                body="Receipts appear here once a booking payment is completed."
                action={<Link to="/find" data-testid="invoices-find" className="btn-primary text-sm">Find parking</Link>} />
            ) : (
              <div className="space-y-4" data-testid="invoices-list">
                {rows.map((inv) => (
                  <div key={inv.invoice_id} className="card-dark p-5" data-testid={`invoice-${inv.invoice_id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="font-bold">{inv.spot_title}</p>
                        <p className="text-xs text-zinc-500 mt-1 font-mono">{inv.invoice_no} · {inv.booking_id}</p>
                        <p className="text-xs text-zinc-500">{fmtDateTime(inv.issued_at)} · {inv.pricing_model} pricing · {(inv.payment_method || "card")}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-display font-bold text-lg">{fmtMoney((inv.total_cents || 0) / 100, (inv.currency || "usd").toUpperCase())}</p>
                        <StatusBadge value={inv.status === "paid" ? "paid" : inv.status} />
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-[#1F2328] space-y-1.5 text-sm" data-testid={`invoice-lines-${inv.invoice_id}`}>
                      {(inv.line_items || []).map((l, i) => (
                        <div key={i} className="flex justify-between text-zinc-400">
                          <span>{l.label}</span>
                          <span>{fmtMoney(((l.amount_cents || 0) + (l.fee_cents || 0)) / 100, (inv.currency || "usd").toUpperCase())}</span>
                        </div>
                      ))}
                      <div className="flex justify-between font-semibold text-white pt-1.5">
                        <span>Total charged</span>
                        <span>{fmtMoney((inv.total_cents || 0) / 100, (inv.currency || "usd").toUpperCase())}</span>
                      </div>
                    </div>
                    {inv.refund && (
                      <div className="mt-3 pt-3 border-t border-[#1F2328] space-y-1.5 text-sm" data-testid={`invoice-refund-${inv.invoice_id}`}>
                        <p className="text-[11px] uppercase tracking-wider text-zinc-500">Refund · {fmtDateTime(inv.refund.refunded_at)}</p>
                        <div className="flex justify-between text-emerald-400">
                          <span>Parking refund</span>
                          <span>-{fmtMoney((inv.refund.parking_refund_cents || 0) / 100, (inv.currency || "usd").toUpperCase())}</span>
                        </div>
                        <div className="flex justify-between text-zinc-500">
                          <span>Service fee (non-refundable)</span>
                          <span>{fmtMoney((inv.refund.non_refundable_service_fee_cents || 0) / 100, (inv.currency || "usd").toUpperCase())}</span>
                        </div>
                        {(inv.refund.non_refundable_processing_cents || 0) > 0 && (
                          <div className="flex justify-between text-zinc-500">
                            <span>Payment processing (non-refundable)</span>
                            <span>{fmtMoney(inv.refund.non_refundable_processing_cents / 100, (inv.currency || "usd").toUpperCase())}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-zinc-300 font-medium pt-1">
                          <span>Net retained by MyRedPark</span>
                          <span>{fmtMoney((inv.refund.net_retained_cents || 0) / 100, (inv.currency || "usd").toUpperCase())}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          {data && pages > 1 && (
            <div className="mt-5 flex items-center justify-between" data-testid="invoices-pager">
              <p className="text-xs text-zinc-500">Page {page} of {pages}</p>
              <div className="flex gap-2">
                <button disabled={page <= 1} onClick={() => { setPage(page - 1); load(page - 1); }} data-testid="invoices-prev" className="btn-secondary !px-3 !py-1.5 text-xs disabled:opacity-40"><ChevronLeft className="w-3.5 h-3.5" /></button>
                <button disabled={page >= pages} onClick={() => { setPage(page + 1); load(page + 1); }} data-testid="invoices-next" className="btn-secondary !px-3 !py-1.5 text-xs disabled:opacity-40"><ChevronRight className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
